"use client";

/**
 * Public component renderer
 * Routes to the same renderers as the builder but without selection ring,
 * toolbar, or hover overlay. Passes isReadOnly to DB-backed renderers.
 */

import type React from "react";
import type { PageComponent } from "../../_types";
import { NAVBAR_HEIGHT_PX } from "../../_logic/constants";
import { getUniqueAnchorId } from "../../_logic/utils";
import { NavbarRenderer } from "../canvas/renderers/NavbarRenderer";
import { HeroRenderer } from "../canvas/renderers/HeroRenderer";
import { CountdownRenderer } from "../canvas/renderers/CountdownRenderer";
import { FaqRenderer } from "../canvas/renderers/FaqRenderer";
import { VenueRenderer } from "../canvas/renderers/VenueRenderer";
import { RichTextRenderer } from "../canvas/renderers/RichTextRenderer";
import { TextButtonRenderer } from "../canvas/renderers/TextButtonRenderer";
import { CarouselRenderer } from "../canvas/renderers/CarouselRenderer";
import { VideoRenderer } from "../canvas/renderers/VideoRenderer";
import { ActivitiesRenderer } from "../canvas/renderers/ActivitiesRenderer";
import { ScheduleRenderer } from "../canvas/renderers/schedule/ScheduleRenderer";
import { TeamRenderer } from "../canvas/renderers/TeamRenderer";
import { PricingRenderer } from "../canvas/renderers/PricingRenderer";
import { CardGridRenderer } from "../canvas/renderers/CardGridRenderer";
import { SponsorsRenderer } from "../canvas/renderers/SponsorsRenderer";
import { DividerRenderer } from "../canvas/renderers/DividerRenderer";
import { FooterRenderer } from "../canvas/renderers/FooterRenderer";

interface PublicComponentRendererProps {
  component: PageComponent;
  /** All page components — needed to generate unique anchor IDs for duplicate labels */
  components: PageComponent[];
}

/**
 * Renders a single component for the public landing page view.
 * No builder UI (selection, toolbar, hover overlay).
 * Wraps each section with an id attribute for anchor scroll navigation.
 */
export function PublicComponentRenderer({
  component,
  components,
}: PublicComponentRendererProps): React.ReactElement | null {
  // Skip invisible navbar
  if (component.type === "navbar" && !component.visible) return null;

  // Render the component
  let rendered: React.ReactElement | null = null;
  switch (component.type) {
    case "navbar":
      return <NavbarRenderer component={component} />;
    case "hero":
      rendered = <HeroRenderer component={component} />;
      break;
    case "countdown":
      rendered = <CountdownRenderer component={component} />;
      break;
    case "faq":
      rendered = <FaqRenderer component={component} />;
      break;
    case "venue":
      rendered = <VenueRenderer component={component} />;
      break;
    case "richText":
      rendered = <RichTextRenderer component={component} />;
      break;
    case "textButton":
      rendered = <TextButtonRenderer component={component} />;
      break;
    case "carousel":
      rendered = <CarouselRenderer component={component} />;
      break;
    case "video":
      rendered = <VideoRenderer component={component} />;
      break;
    case "activities":
      rendered = <ActivitiesRenderer component={component} isReadOnly />;
      break;
    case "schedule":
      rendered = <ScheduleRenderer component={component} isReadOnly />;
      break;
    case "team":
      rendered = <TeamRenderer component={component} />;
      break;
    case "pricing":
      rendered = <PricingRenderer component={component} />;
      break;
    case "cardGrid":
      rendered = <CardGridRenderer component={component} />;
      break;
    case "sponsors":
      rendered = <SponsorsRenderer component={component} />;
      break;
    case "divider":
      rendered = <DividerRenderer component={component} />;
      break;
    case "footer":
      rendered = <FooterRenderer component={component} />;
      break;
    default:
      return null;
  }

  // Wrap with section id for anchor scroll
  // UUID id for navbar links, label-based anchor for CTA SmartButton links
  const anchorId = getUniqueAnchorId(component, components);

  return (
    <div id={component.id}>
      {anchorId && <div id={anchorId} style={{ scrollMarginTop: NAVBAR_HEIGHT_PX }} />}
      {rendered}
    </div>
  );
}
